import React, { useDeferredValue, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import { useCategoryHSCodeContext } from './context/context';

export function CategoryDropDown({ className }: { className?: string }) {
  const {
    setSelectedValueByDropDown,
    selectedValue,
    categoryOptions,
    setComponentStateSearch,
    open,
  } = useCategoryHSCodeContext();

  const ref = useRef<HTMLDivElement>(null);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      const nextOption = e.currentTarget.nextElementSibling as HTMLButtonElement;
      if (nextOption && nextOption.getAttribute('role') === 'option') {
        nextOption.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const prevOption = e.currentTarget.previousElementSibling as HTMLButtonElement;
      if (prevOption && prevOption.getAttribute('role') === 'option') {
        prevOption.focus();
      }
    }
  };

  const isOpenForFocus = useDeferredValue(open);

  useEffect(() => {
    if (isOpenForFocus) {
      const firstOption = ref.current?.querySelector('button');
      if (firstOption) {
        firstOption.focus();
      }
    }
  }, [isOpenForFocus]);

  return (
    <div
      ref={ref}
      className={twMerge('max-w-[300px] min-w-[280px] flex flex-col gap-0', className)}
      role="listbox"
    >
      <div className="w-full min-w-[180px] overflow-y-scroll max-h-[300px]">
        {!categoryOptions.length ? (
          <div className="px-4 py-2 text-ink-900 text-base">
            <FormattedMessage id="shipments.category-hs-codes.no-results" />
          </div>
        ) : (
          categoryOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={selectedValue?.value === opt.value}
              className={twMerge(
                'w-full text-left py-2 text-base px-4  cursor-pointer text-ink-900 hover:bg-teal-100 transition-colors focus:bg-teal-200 outline-none whitespace-nowrap overflow-hidden text-ellipsis',
                selectedValue?.value === opt.value &&
                  'bg-teal-500 text-white hover:bg-teal-500 focus:bg-teal-500'
              )}
              onClick={() => {
                setSelectedValueByDropDown(opt);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedValueByDropDown(opt);
                  return;
                }
                handleOptionKeyDown(e);
              }}
            >
              {opt.label}
            </button>
          ))
        )}
      </div>
      <button
        className="text-ink-900 text-base whitespace-nowrap border-t border-sky-300 pl-4 py-3 mt-0 text-left"
        aria-label="cant-find-right-one"
        type="button"
        onClick={setComponentStateSearch}
      >
        <FormattedMessage
          id="shipments.category-hs-codes.cannot-find-right-one"
          values={{
            // eslint-disable-next-line react/no-unstable-nested-components
            a: (chunks: string) => <span className="text-blue-500 pl-1">{chunks}</span>,
          }}
        />
      </button>
    </div>
  );
}
